import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const currentCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategoryName} - 大满贯App`,
    description: `大满贯App${currentCategoryName}栏目，提供最新最全面的体育赛事内容。`,
    url: `https://damanjuapp.com/category/${activeCategory}`,
    isPartOf: { '@type': 'WebSite', name: '大满贯App' },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://damanjuapp.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategoryName} - 体育赛事资讯`}
        description={`大满贯App${currentCategoryName}栏目，提供最新最全面的体育赛事资讯、深度分析、技巧攻略等内容。涵盖足球、篮球、网球、电竞等热门体育项目。`}
        keywords={`大满贯,${currentCategoryName},体育资讯,赛事分析,体育新闻`}
        canonicalUrl={`https://damanjuapp.com/category/${activeCategory}`}
        schema={categorySchema}
      />
      <Breadcrumb />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Category Tabs */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-6">
            赛事资讯
          </h1>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => {
                  setActiveCategory(cat.slug);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/20'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Article Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group rounded-2xl overflow-hidden bg-background-100 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      {article.categoryName}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="text-base font-bold font-heading text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                    {article.title}
                  </h2>
                  <p className="text-foreground-600 text-sm line-clamp-2 mb-4 leading-relaxed flex-1">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400 pt-3 border-t border-background-200/70">
                    <span>{article.date}</span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                        阅读
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600">该分类下暂无文章</p>
          </div>
        )}
      </div>
    </>
  );
}